import Vue from 'vue';
import Vuex from 'vuex';

import Product from '@/types/Product';
import Item from '@/types/Item';
import Order from '@/types/Order';
import OrderItem from '@/types/OrderItem';
import User from '@/types/User';
import Character from '@/types/Character';
import Shipping from '@/types/Shipping';
import Paying from '@/types/Paying';
import Discount from '@/types/Discount';
import Tuple from '@/types/Tuple';
import Tag from '@/types/Tag';
import CollectedProduct from '@/types/CollectedProduct';
import util from './util/Util';
import { UserRole } from './types/UserRole';
import Country from './types/Country';

Vue.use(Vuex);

const defaultLanguage = 'sv';

export default new Vuex.Store({
  state: {
    language: defaultLanguage,
    products: new Array<Product>(),
    items: new Array<Item>(),
    characters: new Array<Character>(),
    order: new Order(),
    user: new User(),
    shippings: new Array<Shipping>(),
    payings: new Array<Paying>(),
    mostPopularCharacterTuples: new Array<Tuple>(),
    mostPopularItems: new Array<Item>(),
    tags: new Array<Tag>(),
    countries: [
      {
          name: 'Sverige',
          countryCode: 'SE',
          languageCode: 'sv',
          currencyCode: 'SEK',
          currencySymbol: 'kr',
          meta: {
            siteLanguage: true,
            shippingCountry: true,
            collectionCurrency: true
          }
      },
      {
          name: 'Norge',
          countryCode: 'NO',
          languageCode: 'no',
          currencyCode: 'NOK',
          currencySymbol: 'kr',
          meta: {
            siteLanguage: true,
            shippingCountry: true,
            collectionCurrency: true
          }
      },
      {
          name: 'Finland',
          countryCode: 'FI',
          languageCode: 'fi',
          currencyCode: 'EUR',
          currencySymbol: '€',
          meta: {
            siteLanguage: true,
            shippingCountry: true,
            collectionCurrency: true
          }
      },
      {
          name: 'Danmark',
          countryCode: 'DK',
          languageCode: 'dk',
          currencyCode: 'DKK',
          currencySymbol: 'kr',
          meta: {
            siteLanguage: false,
            shippingCountry: true,
            collectionCurrency: false
          }
      },
      {
          name: 'England',
          countryCode: 'UK',
          languageCode: 'en',
          currencyCode: 'GBP',
          currencySymbol: '£',
          meta: {
            siteLanguage: true,
            shippingCountry: false,
            collectionCurrency: false
          }
      }
    ]
  },

  getters: {
    products(state): Product[] {
      return state.products;
    },

    productById: (state: any) => (productId: string): Product => {
      return state.products.find( (product: Product) => product.productId === productId);
    },

    productsByType: (state: any) => (productType: string): Product => {
      return state.products.filter( (product: Product) => product.productType === productType);
    },

    getMostLikedProducts: (state: any) => (limit: number): Product[] => {
      const products = [...state.products];
      return products.sort((a: Product, b: Product) => b.numberLikes - a.numberLikes).slice(0, limit);
    },

    numberOrderItems(state, getters): number {
      return getters.orderItems ?
        getters.orderItems.reduce( (accumulator: number, item: OrderItem) => accumulator + item.quantity, 0 ) : 0;
    },

    itemById: (state: any) => (itemId: string): Item => {
      return state.items.find( (item: Item) => item.itemId === itemId);
    },

    characterById: (state: any) => (characterId: string): Character => {
      return state.characters.find((character: Character) => character.characterId === characterId);
    },

    orderItems(state: any) {
      return state.order.orderItems;
    },

    orderId(state: any) {
      return state.order.orderId;
    },

    getOrderItem: (state: any) => (itemId: string): OrderItem => {
      return state.order.orderItems.find((orderItem: OrderItem) => orderItem.itemId === itemId);
    },

    collectedProducts(state): CollectedProduct[] {
      return state.user.collectedProducts;
    },

    notCollectedProducts(state): Product[] {
      if (state.user.collectedProducts) {
        const collectedProductIds = state.user.collectedProducts.map((product: CollectedProduct) => product.productId);
        return state.products.filter((product: Product) => !collectedProductIds.includes(product.productId));
      }
      return state.products;
    },

    numberProductsCollection(state, getters): number {
      return getters.collectedProducts ? getters.collectedProducts.length : 0;
    },

    isCollected: (state, getters) => (productId: string): boolean => {
      return getters.collectedProducts &&
        getters.collectedProducts.find((product: CollectedProduct) => product.productId === productId);
		},

    likedProducts(state): Product[] {
      return state.user.likedProducts;
    },

    numberProductLikes(state, getters): number {
      return getters.likedProducts ? getters.likedProducts.length : 0;
    },

		isLiked: (state, getters) => (productId: string): boolean => {
			return getters.likedProducts && getters.likedProducts.find((product: Product) => product.productId === productId);
		},

    getUserRole(state): string {
      return state.user.role;
    },

    isUserAdmin(state): boolean {
      return state.user.role === UserRole.Admin;
    },

    isSignedIn(state): boolean {
      return util.isObjectId(state.user.userId);
    },

    shippingById: (state: any) => (shippingId: string): Shipping => {
      return state.shippings.find( (shipping: Shipping) => shipping.shippingId === shippingId);
    },

    getCurrencySymbol: (state: any) => (currencyCode: string): string => {
      return state.countries.find((country: Country) => country.currencyCode === currencyCode).currencySymbol;
    },

    getLanguage: (state: any) => (languageMap: any): string => {
      return languageMap[state.language] != null ? languageMap[state.language] as string : languageMap[defaultLanguage] as string;
    },

    getSiteSupportedLanguageCountries(state): Country[] {
      return state.countries.filter((country: Country) => country.meta.siteLanguage);
    },

    getShippingCountries(state): Country[] {
      return state.countries.filter((country: Country) => country.meta.shippingCountry);
    },

    getCollectionSupportedLanguageCountries(state): Country[] {
      return state.countries.filter((country: Country) => country.meta.collectionCurrency);
    }
  },

  mutations: {
    setUser(state, user: User) {
      if (!user.collectedProducts) {
        user.collectedProducts = new Array<CollectedProduct>();
      }
      if (!user.likedProducts) {
        user.likedProducts = new Array<Product>();
      }
      state.user = user;
    },

    setOrder(state, order: Order) {
      state.order = order;
    },

    setDiscount(state, discount: Discount) {
      state.order.discount = discount;
    },

    setProducts(state, products: Product[]) {
      state.products = products;
    },

    setProduct(state, newProduct: Product) {
      state.products.map((product) => product.productId === newProduct.productId ? newProduct : product);
    },

    setItem(state, newItem: Item) {
      state.items.map((item) => item.itemId === newItem.itemId ? newItem : item);
    },

    setCharacters(state, characters: Character[]) {
      state.characters = characters;
    },

    setShippings(state, shippings: Shipping[]) {
      state.shippings = shippings;
    },

    setTags(state, tags: Tag[]) {
      state.tags = tags;
    },

    setPayings(state, payings: Paying[]) {
      state.payings = payings;
    },

    changeLanguage(state, language: string) {
      state.language = language;
    },

    setItems(state, items: Item[]) {
      state.items = items;
    },

    setMostPopularCharacterTuples(state, mostPopularCharacterTuples: Tuple[]): void {
      state.mostPopularCharacterTuples = mostPopularCharacterTuples;
    },

    setMostPopularItems(state, mostPopularItems: Item[]): void {
      state.mostPopularItems = mostPopularItems;
    }
  },

  actions: {
    changeLanguage({ commit }, language: string) {
      commit('changeLanguage', language);
    },

    setProducts({ commit }, products: Product[]) {
      commit('setProducts', products);
    },

    setItems({ commit }, items: Item[]) {
      commit('setItems', items);
    },

    setCharacters({ commit }, characters: Character[]) {
      commit('setCharacters', characters);
    }
  },
});
